import { useCallback } from 'react';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSetAgentCanvasToolbarSlot } from '@/components/agentCanvasToolbarSlot';
import { useDevBannerOverrides } from '@/lib/devBannerTriggers';
import { CANVAS_RAIL_WIDTH, PILL_GUTTER } from './railConstants';
import {
  PAPER_CHROME_RADIUS_CLASS,
  PAPER_ICON_CONTROL_CLASS,
  PAPER_ICON_CLASS,
  PAPER_ICON_STROKE_WIDTH,
} from './paperShapes';

interface PaperArtifactRailProps {
  artifactOpen: boolean;
  onOpen: () => void;
  topbarHeightPx?: number;
}

const IconButton = ({
  children,
  title,
  onClick,
  ariaExpanded,
  ariaControls,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  ariaExpanded?: boolean;
  ariaControls?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={title}
    aria-expanded={ariaExpanded}
    aria-controls={ariaControls}
    className={cn(
      PAPER_ICON_CONTROL_CLASS,
    )}
  >
    {children}
  </button>
);

export const PaperArtifactRail = (props: PaperArtifactRailProps) => {
  const topbarHeight = props.topbarHeightPx ?? 56;
  const setCanvasSlot = useSetAgentCanvasToolbarSlot();
  const pill = useDevBannerOverrides().railPill;
  const slotRef = useCallback(
    (el: HTMLDivElement | null) => setCanvasSlot(el),
    [setCanvasSlot],
  );

  // The rail flips between two distinct, single-purpose controls based on
  // whether the panel is open:
  //   - panel CLOSED -> a tiny pill that just holds the open-panel
  //     chevron at topbar height. Nothing else is on-screen on the
  //     right edge.
  //   - panel OPEN  -> a compact pill that holds only the canvas tool
  //     stack. The panel's close (X) lives in the panel chrome itself
  //     (next to the export buttons) so the canvas pill stays focused
  //     on what it's for: drawing on the panel.
  // Rendering one of these two shapes (instead of one always-tall rail
  // with conditional empty space) is what gives the canvas tools their
  // tight, no-empty-room outline.
  if (!props.artifactOpen) {
    return (
      <aside
        aria-label="Panel controls"
        className={cn(
          'flex flex-col shrink-0 relative z-[3] overflow-hidden',
          pill
            ? cn('self-center my-[16px] bg-[var(--surface)] border border-solid border-[var(--hair)]', PAPER_CHROME_RADIUS_CLASS)
            : 'h-full bg-[var(--bg)] border-l border-solid border-[var(--hair)]',
        )}
        style={{ width: CANVAS_RAIL_WIDTH, marginLeft: pill ? PILL_GUTTER : undefined, marginRight: pill ? PILL_GUTTER : undefined }}
      >
        <div
          className="flex flex-col items-center justify-start shrink-0 pt-[10px]"
          style={{ height: topbarHeight, boxSizing: 'border-box' }}
        >
          <IconButton
            title="Open panel"
            onClick={props.onOpen}
            ariaExpanded={false}
            ariaControls="paper-artifact-panel"
          >
            <ChevronLeft className={PAPER_ICON_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
          </IconButton>
        </div>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Agent canvas tools"
      className={cn(
        'flex flex-col shrink-0 relative z-[3] overflow-hidden',
        pill
          ? cn('self-center my-[16px] bg-[var(--surface)] border border-solid border-[var(--hair)]', PAPER_CHROME_RADIUS_CLASS)
          : 'h-full bg-[var(--bg)] border-l border-solid border-[var(--hair)]',
      )}
      style={{ width: CANVAS_RAIL_WIDTH, marginLeft: pill ? PILL_GUTTER : undefined, marginRight: pill ? PILL_GUTTER : undefined }}
    >
      {/* Agent Canvas toolbar slot — populated via portal by
          AgentCanvasOverlay. The slot fills the whole rail because the
          chevron/X are not part of this pill anymore. */}
      <div ref={slotRef} className="flex-1 flex flex-col min-h-0" />
    </aside>
  );
};
